import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axiosAPI from "../axiosAPI";

function buildExpensesQuery(filter = {}) {
  const params = new URLSearchParams();
  if (filter.start_date) params.set("start_date", filter.start_date);
  if (filter.end_date) params.set("end_date", filter.end_date);
  if (filter.category) params.set("category", filter.category);
  if (filter.lawsuit) params.set("lawsuit_id", filter.lawsuit);
  if (filter.clientId) params.set("clientId", filter.clientId);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const useExpense = (filter = {}) => {
  const queryClient = useQueryClient();
  const queryString = buildExpensesQuery(filter);

  const query = useQuery({
    queryKey: ["expenses", "list", queryString],
    queryFn: async () => {
      const { data } = await axiosAPI.get(`/expenses${queryString}`);
      return data;
    },
  });

  const invalidateExpenses = () => {
    queryClient.invalidateQueries({ queryKey: ["expenses"] });
    queryClient.invalidateQueries({ queryKey: ["financial"] });
  };

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await axiosAPI.post("/expenses", payload);
      return data;
    },
    onSuccess: () => {
      invalidateExpenses();
      toast.success("Despesa cadastrada.", { position: "bottom-right" });
    },
    onError: () => {
      toast.error("Ops, ocorreu um erro ao cadastrar a despesa.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data } = await axiosAPI.put(`/expenses/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      invalidateExpenses();
      toast.success("Despesa atualizada.", { position: "bottom-right" });
    },
    onError: () => {
      toast.error("Ops, ocorreu um erro ao atualizar a despesa.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (expenseId) => {
      const { data } = await axiosAPI.delete(`/expenses/${expenseId}`);
      return data;
    },
    onSuccess: () => {
      invalidateExpenses();
      toast.success("Despesa excluída.", { position: "bottom-right" });
    },
    onError: () => {
      toast.error("Ops, ocorreu um erro ao excluir a despesa.");
    },
  });

  return {
    expenses: query.data?.expenses ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.error,
    refetch: query.refetch,
    createExpense: createMutation.mutateAsync,
    updateExpense: updateMutation.mutateAsync,
    deleteExpense: deleteMutation.mutateAsync,
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
    isMutating:
      createMutation.isLoading ||
      updateMutation.isLoading ||
      deleteMutation.isLoading,
  };
};
